// src/pages/Employees.tsx
import React, { useEffect, useRef, useState } from "react";
import Table, { Column } from "../../components/Table";
import Modal from "../../components/Modal";
import FormBuilder, { FieldConfig } from "../../components/FormBuilder";
import { employeeFields } from "./formFields";
import { mapProfileToRow, TableRow } from "../../services/mappers";
import {
  deleteProfile,
  getProfiles,
  updateProfile,
} from "../../services/profileService";
import { addProfile } from "../../services/profileService";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { flattenProfile } from "../../services/mappers";
import Button from "../../components/Button";
import { useAuth } from "../../context/AuthContext";
import InputField from "../../components/InputField";
import { Icon } from "@iconify/react";
import axiosClient from "../../api/axiosClient";

export default function EmployeesPage() {
  const { user, isAuthenticated } = useAuth();
  const [data, setData] = useState<TableRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string[]> | null>(
    null,
  );
  const formRef = useRef<any>(null);
  const navigate = useNavigate();
  const [editingEmployee, setEditingEmployee] = useState<TableRow | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<TableRow | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  // Search + filter state
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const debounceRef = useRef<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
      return;
    }

    if (user?.role !== "Admin") {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Debounce searchTerm -> debouncedSearch after 500ms
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 500);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  // immediate apply on Enter
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      setDebouncedSearch(searchTerm.trim());
    }
  };

  // centralised loader that uses the search endpoint when query or status is present
  const fetchProfiles = async () => {
    setLoading(true);
    try {
      // If there is a query or a non-default status, call search endpoint
      const useSearch =
        (debouncedSearch && debouncedSearch.length > 0) ||
        (statusFilter && statusFilter !== "all");

      if (useSearch) {
        const params: any = {};
        if (debouncedSearch) params.query = debouncedSearch;
        if (statusFilter && statusFilter !== "all")
          params.status = statusFilter;
        // optional: add pagination params here if needed (page, per_page)
        const res = await axiosClient.get("/profiles/search", { params });
        // expected: { profiles: [ ... ], total?: number } or { data: [...] } - adapt if your API differs
        const payload = res.data ?? {};
        // support two common shapes:
        const rawProfiles = Array.isArray(payload.profiles)
          ? payload.profiles
          : Array.isArray(payload.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
        const rows = rawProfiles.map(mapProfileToRow);
        setData(rows.filter((r: any) => r.role === "Employee"));
      } else {
        // fallback to existing getProfiles()
        const res = await getProfiles();
        const rows = (res.profiles || []).map(mapProfileToRow);
        const employees = rows.filter((r) => r.role === "Employee");
        setData(employees);
      }
    } catch (err: any) {
      console.error("Failed to fetch profiles", err);
      toast.error(err?.message || "Failed to load employees");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // call fetchProfiles on mount and whenever filters change
  useEffect(() => {
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter]);

  // original helpers (unchanged)
  const getEmployeeFields = (isEdit: boolean) => {
    const filtered = employeeFields
      // return employeeFields
      .filter((f) => {
        if (!("name" in f)) return true;
        if (isEdit && ["password", "password_confirmation"].includes(f.name)) {
          return false;
        }
        return true;
      })
      .map((f) => {
        if (!("name" in f)) return f;
        if (isEdit && f.name === "username") {
          return { ...f, readOnly: true };
        }
        return f;
      });
    const cleaned: FieldConfig[] = [];
    for (let i = 0; i < filtered.length; i++) {
      const item = filtered[i];
      if (!("name" in item)) {
        const next = filtered[i + 1];
        if (!next || !("name" in next)) continue;
      }
      cleaned.push(item);
    }
    return cleaned;
  };

  const load = async () => {
    // keep for manual reloads (after add/edit/delete)
    await fetchProfiles();
  };

  useEffect(() => {
    // initial load already covered by effect above but keep for safety
    // fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: Column<TableRow>[] = [
    {
      header: "Employee",
      accessor: "name",
      cell: (row) => (
        <div className="flex items-center gap-3 cursor-pointer">
          <div className="relative">
            {row.profile ? (
              <img
                src={row.profile}
                alt={row.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500">
                <span className="text-white font-semibold">
                  {row.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}

            {(row.raw.status === "Active" || row.raw.status === "Inactive") && (
              <span
                className={`absolute top-0 right-0 w-3 h-3 border-2 border-white rounded-full ${
                  row.raw.status === "Active" ? "bg-green-500" : "bg-red-500"
                }`}
              ></span>
            )}
          </div>

          <div>
            <div className="font-medium">{row.name}</div>
            {row.email && (
              <div className="text-xs text-gray-400">{row.email}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      header: "Mobile",
      accessor: "mobile_number",
      cell: (row) =>
        row.mobile_number ?? <span className="text-sm text-gray-400">—</span>,
    },
    {
      header: "Code",
      accessor: "id",
      width: "w-28",
      cell: (row) =>
        row.raw.employee_code ?? (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
  ];

  const handleAddEmployee = async (formData: any) => {
    setFormErrors(null);
    try {
      const data = new FormData();
      if (!formData.role || formData.role.trim() === "") {
        formData.role = "Employee";
      }
      if (formData.employee_code_option === "Generate") {
        delete formData.employee_code;
      } else if (formData.employee_code_option === "Enter") {
        if (
          formData.employee_code &&
          !formData.employee_code.startsWith("CUS")
        ) {
          formData.employee_code = `EMP${formData.employee_code}`;
        }
      }

      Object.entries(formData).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (value instanceof FileList && value.length > 0) {
          data.append(key, value[0]);
        } else if (value instanceof File) {
          data.append(key, value);
        } else if (
          Array.isArray(value) &&
          value.length > 0 &&
          value[0] instanceof File
        ) {
          data.append(key, value[0]);
        } else if (typeof value === "object") {
          data.append(key, JSON.stringify(value));
        } else {
          data.append(key, value.toString());
        }
      });

      const res = await addProfile(data);
      toast.success(res.message || "Employee added successfully!");
      setOpenModal(false);
      await load();
      formRef.current?.reset();
    } catch (err: any) {
      const errorData = err?.response?.data;
      if (errorData) {
        setFormErrors(errorData.errors ?? null);
        const messages = [
          errorData.message,
          ...Object.values(errorData.errors || {}).flat(),
        ]
          .filter(Boolean)
          .join(", ");
        toast.error(messages);
      } else {
        toast.error(err?.message || "Failed to add employee");
      }
      console.error(err);
    }
  };

  const handleUpdateEmployee = async (formData: any) => {
    if (!editingEmployee) return;
    setFormErrors(null);
    try {
      const id = editingEmployee.raw?.profile_id ?? editingEmployee.id;
      const allowedFields = [
        "email",
        "mobile_number",
        "first_name",
        "last_name",
        "date_of_birth",
        "gender",
        "marital_status",
        "father_name",
        "mother_name",
        "spouse_name",
        "aadhaar_number",
        "pan_number",
        "driving_license",
        "voter_id",
        "passport_number",
        "salary",
        "employment_type",
        "employer_name",
        "work_experience",
        "credit_score",
        "monthly_income",
        "other_liabilities",
        "status",
        "profile_photo",
        "sign_photo",
      ];
      const filteredData: any = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (allowedFields.includes(key)) {
          filteredData[key] = value;
        }
      });
      const data = new FormData();
      Object.entries(filteredData).forEach(([key, value]) => {
        if (value === null || value === undefined) return;

        if (value instanceof FileList && value.length > 0) {
          data.append(key, value[0]);
        } else if (value instanceof File) {
          data.append(key, value);
        } else if (
          Array.isArray(value) &&
          value.length > 0 &&
          value[0] instanceof File
        ) {
          data.append(key, value[0]); // or append all if backend supports
        } else {
          data.append(key, String(value));
        }
      });

      const res = await updateProfile(id, data);
      toast.success(res.message || "Employee updated successfully!");
      setOpenModal(false);
      setEditingEmployee(null);
      await load();
    } catch (err: any) {
      const errorData = err?.response?.data;
      if (errorData) {
        setFormErrors(errorData.errors ?? null);
        const messages = [
          errorData.message,
          ...Object.values(errorData.errors || {}).flat(),
        ]
          .filter(Boolean)
          .join(", ");
        toast.error(messages);
      } else {
        toast.error(err?.message || "Failed to update employee");
      }
      console.error(err);
    }
  };

  const handleDeleteClick = (row: TableRow) => {
    setEmployeeToDelete(row);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!employeeToDelete) return;
    setDeleting(true);
    try {
      const id = employeeToDelete.raw?.profile_id ?? employeeToDelete.id;
      const res = await deleteProfile(id);
      toast.success(res.message || "Employee deleted successfully!");
      setDeleteModalOpen(false);
      setEmployeeToDelete(null);
      await load();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to delete employee");
    } finally {
      setDeleting(false);
    }
  };
  // when user cancels / closes the modal
  const handleCloseModal = () => {
    // clear API validation errors shown below inputs
    setFormErrors(null);

    // reset RHF form and clear its internal errors and previews
    if (formRef.current) {
      // formRef.current.reset() is exposed by FormBuilder
      // and clearErrors() if you exposed it too
      (formRef.current as any).reset();
      if ((formRef.current as any).clearErrors) {
        (formRef.current as any).clearErrors();
      }
    }

    setOpenModal(false);
    setEditingEmployee(null);
  };

  const buildDefaultValues = (fields: FieldConfig[]) => {
    const defaults: any = {};
    fields.forEach((field) => {
      if ("name" in field && field.defaultValue !== undefined) {
        defaults[field.name] = field.defaultValue;
      }
    });
    return defaults;
  };

  return (
    <div className="pt-3">
      <div className="grid grid-cols-1 md:grid-cols-2 mb-4 gap-4 items-center light-bg-color p-4 rounded-xl shadow-sm">
        <div className="flex justify-between">
          <h2 className="text-2xl font-semibold text-color ">Employees</h2>
          {/* Filter Dropdown */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white border border-gray-200 shadow-sm rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 focus:outline-none transition"
          >
            <option value="all">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div className="">
          {/* Search Input */}
          <div className="relative w-full ">
            <InputField
              placeholder="Search (name, code, etc...)"
              className="w-full focus:outline-none focus:ring-2 focus:ring-sky-300 transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon icon="mdi:magnify" width={18} className="text-slate-400" />
            </div>
          </div>
        </div>
      </div>

      <Table<TableRow>
        data={data}
        columns={columns}
        onDelete={handleDeleteClick}
        showActions={true}
        defaultPageSize={10}
        pageSizeOptions={[5, 10, 25]}
        loading={loading}
        onRowClick={(row) =>
          navigate("/employees/details", { state: { id: row.id } })
        }
        onAdd={() => {
          setEditingEmployee(null);
          setOpenModal(true);
        }}
        onEdit={async (row) => {
          const maybeProfile = row.raw ?? row;
          const flattened = maybeProfile.profile_id
            ? flattenProfile(maybeProfile)
            : maybeProfile;
          setEditingEmployee({ ...row, raw: flattened });
          setOpenModal(true);
        }}
      />

      {/* Modal Form */}
      <Modal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title={editingEmployee ? "Edit Employee" : "Add Employee"}
      >
        <FormBuilder
          ref={formRef}
          fields={getEmployeeFields(!!editingEmployee)}
          // defaultValues={editingEmployee?.raw}
          onSubmit={editingEmployee ? handleUpdateEmployee : handleAddEmployee}
          // onCancel={() => setOpenModal(false)}
          onCancel={handleCloseModal}
          submitLabel={editingEmployee ? "Update Employee" : "Add Employee"}
          error={formErrors}
          defaultValues={
            editingEmployee
              ? editingEmployee?.raw
              : buildDefaultValues(getEmployeeFields(!!editingEmployee))
          }
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Delete"
      >
        <p className="mb-5 text-gray-700">
          Are you sure you want to delete{" "}
          <span className="font-semibold">{employeeToDelete?.name}</span>?
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => setDeleteModalOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
